import type { ComponentType, ReactNode } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  /** Small trailing tag ("Recomendado", "Próximamente"). */
  badge?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * A selectable card for choosing one option out of a few (tone, mode, how something works). Spells
 * each option out instead of hiding it behind a dropdown, which is what less technical users need.
 * Put several in a `role="radiogroup"` grid.
 */
export function ChoiceCard({
  selected,
  onSelect,
  title,
  description,
  icon: Icon,
  badge,
  disabled,
  className,
}: ChoiceCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-xl border bg-card p-4 text-left transition-all outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary dark:bg-primary/10'
          : 'border-border hover:border-foreground/20 hover:bg-muted/40',
        className,
      )}
    >
      {Icon && (
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors',
            selected
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground group-hover:text-foreground',
          )}
        >
          <Icon className="size-4" />
        </span>
      )}
      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
          {title}
          {badge}
        </span>
        {description && <span className="block text-sm text-pretty text-muted-foreground">{description}</span>}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
        )}
      >
        {selected && <Check className="size-3" />}
      </span>
    </button>
  );
}
